import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface ModalConfig {
  id: string;
  title?: string;
  component?: any;
  data?: any;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen';
  centered?: boolean;
  backdrop?: boolean | 'static';
  keyboard?: boolean;
  animation?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private modals: { [key: string]: BehaviorSubject<boolean> } = {};
  private modalConfig = new BehaviorSubject<ModalConfig | null>(null);
  
  public modalConfig$ = this.modalConfig.asObservable();
  
  constructor() { }
  
  // Register a modal with the service
  register(id: string): void {
    if (this.modals[id]) {
      console.warn(`Modal with id ${id} is already registered.`);
      return;
    }
    
    this.modals[id] = new BehaviorSubject<boolean>(false);
  }
  
  // Unregister a modal from the service
  unregister(id: string): void {
    if (!this.modals[id]) {
      console.warn(`Modal with id ${id} is not registered.`);
      return;
    }
    
    delete this.modals[id];
  }
  
  // Open a modal with the given id and configuration
  open(config: ModalConfig): void {
    const id = config.id;
    
    if (!this.modals[id]) {
      this.register(id);
    }
    
    this.modalConfig.next(config);
    this.modals[id].next(true);
  }
  
  // Close a modal with the given id
  close(id: string): void {
    if (!this.modals[id]) {
      console.warn(`Modal with id ${id} is not registered.`);
      return;
    }
    
    this.modals[id].next(false);
    this.modalConfig.next(null);
  }
  
  // Check if a modal is open
  isOpen(id: string): Observable<boolean> {
    if (!this.modals[id]) {
      this.register(id);
    }
    
    return this.modals[id].asObservable();
  }
  
  // Get the current modal configuration
  getConfig(): Observable<ModalConfig | null> {
    return this.modalConfig$;
  }
}